// Ohio University: public college page (#2652), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Ohio University posts no Common Data Set anyone can open: its Office of
//    Institutional Effectiveness and Analytics links a "2025 Common Data Set"
//    to a SharePoint file that asks for an Ohio University sign-in, and its
//    historical CDS files are "under review". The first-year counts
//    therefore come from the U.S. Department of Education's IPEDS
//    Admissions survey for Fall 2024 (College Navigator and the ADM2024
//    complete data file, unit 204857, Ohio University-Main Campus, the
//    Athens campus).
//  - ohio.edu/admissions: Dates and Deadlines (freshman priority dates; the
//    page carries no year, and the OHIO Admission Promise page confirms the
//    November 15, 2026 early action deadline for fall 2027), How to Apply as
//    a Freshman, the Test-Optional Admission Pathway, Programs with More
//    Selective Admission Guidelines for Freshmen, and The OHIO Admission
//    Promise for Fall 2027.
//  - Office of Student Financial Aid and Scholarships, Cost of Attendance:
//    The OHIO Guarantee 2026-2027 Cohort, Athens campus.
//  - College Scorecard, school id 204857 ("Ohio University-Main Campus"),
//    net price by family income, read from the Scorecard API on 2026-10-01.
// Cost: the 2026-2027 cohort's cost of attendance, the latest Ohio posts.
// Under the OHIO Guarantee those direct costs stay fixed for that cohort for
// four years; the fall 2027 cohort's rates are not posted yet, which the
// cost note says. Out-of-state tuition and fees are the in-state $15,018
// plus $11,774 in out-of-state fees.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    ipeds: {
        label: 'IPEDS Admissions, Fall 2024, U.S. Department of Education',
        url: 'https://nces.ed.gov/collegenavigator/?id=204857#admsns',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'Ohio University Admissions, Dates and Deadlines',
        url: 'https://www.ohio.edu/admissions/apply/dates-deadlines',
        read: '2026-10-01',
    },
    apply: {
        label: 'Ohio University Admissions, How to Apply as a Freshman',
        url: 'https://www.ohio.edu/admissions/freshman/apply',
        read: '2026-10-01',
    },
    testOptional: {
        label: 'Ohio University Admissions, Test-Optional Admission Pathway',
        url: 'https://www.ohio.edu/admissions/apply/test-optional',
        read: '2026-10-01',
    },
    selective: {
        label: 'Ohio University Admissions, Programs with More Selective Admission Guidelines for Freshmen',
        url: 'https://www.ohio.edu/admissions/freshman/selective-programs',
        read: '2026-10-01',
    },
    promise: {
        label: 'Ohio University Admissions, The OHIO Admission Promise for Fall 2027',
        url: 'https://www.ohio.edu/admissions/ohio-admission-promise',
        read: '2026-10-01',
    },
    costs: {
        label: 'Ohio University Financial Aid and Scholarships, Cost of Attendance',
        url: 'https://www.ohio.edu/financial-aid/cost',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?204857-Ohio-University-Main-Campus',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'ohio-university',
    kbId: 'ohio_university',
    name: 'Ohio University',
    shortName: 'Ohio University',
    aliases: ['OHIO', 'OU'],
    location: 'Athens, OH',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Ohio University brand colors); decoration only.
    branding: {
        primary: '#00694E',
        secondary: '#FFFFFF',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/ohio_university_logo.png',
    },

    admission: {
        entering: 'Fall 2024',
        cdsCycle: null,
        applied: 27486,
        admitted: 23360,
        enrolled: 4489,
        source: 'ipeds',
        where: 'first-time, degree-seeking undergraduates, Athens campus',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Ohio University publishes no first-year acceptance rate by college or major, and its Common Data Set is not open to the public, so this page uses the federal IPEDS count for the whole Athens campus.',
        source: 'ipeds',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Admission to every Ohio University program is selective and holistic. Some colleges and majors ask for more:',
        rows: [
            { school: 'OHIO Admission Promise', requirement: 'Apply by November 15, 2026, show a minimum 3.0 cumulative GPA on a strong college prep curriculum, and enroll full-time on the Athens campus in fall 2027 for automatic general admission, plus a renewable award of at least $2,000 if Ohio receives your FAFSA results by January 15, 2027. A more selective program still reviews you separately.', source: 'promise' },
            { school: 'Honors Tutorial College', requirement: 'HTC-specific essays are required, and many HTC majors strongly recommend more materials. Students selected to move forward must interview, typically in January and February.', source: 'selective' },
            { school: 'College of Business', requirement: 'A selective, holistic review. Successful applicants typically present a 3.0 or higher GPA in a strong college preparatory curriculum. An essay on your preparation for business study is encouraged, not required.', source: 'selective' },
            { school: 'Engineering, computer science and artificial intelligence majors', requirement: 'Four years of college preparatory math; one year of chemistry or physics plus one more year of chemistry, physics or an engineering science; and above-average grades in math and science.', source: 'selective' },
            { school: 'Biological Sciences', requirement: 'Strong preparation in mathematics and science, shown by your coursework.', source: 'selective' },
            { school: 'School of Nursing', requirement: 'A high school GPA of 3.0 or higher to be considered.', source: 'selective' },
            { school: 'E.W. Scripps School of Journalism', requirement: 'Strong preparation in writing and language, shown by your coursework; media experience is also considered, and an essay is encouraged, not required.', source: 'selective' },
            { school: 'Art + Design (B.F.A.), Visual Communication and Film', requirement: 'A portfolio review.', source: 'selective' },
            { school: 'Music and Dance', requirement: 'A successful audition.', source: 'selective' },
            { school: 'Theater (B.F.A.)', requirement: 'An audition, interview and/or portfolio review.', source: 'selective' },
            { school: 'Aviation Flight', requirement: 'A supplemental application.', source: 'selective' },
        ],
        note: 'You apply with the Common App or Ohio\'s own application, a $55 fee and an official high school transcript. Essays, letters of recommendation and activity lists are optional.',
        source: 'apply',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Early action', value: 'November 15' },
        rows: [
            { plan: 'Early action', deadline: 'November 15', decision: null, binding: false },
            { plan: 'Rolling admission', deadline: 'February 1', decision: null, binding: false },
        ],
        note: 'Applicants who meet the early action deadline get priority consideration for direct entry to academic programs, merit scholarships and honors and scholars programs. Ohio lists these as freshman priority dates and does not publish decision dates.',
        source: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Freshman applicants to the Athens campus may apply on Ohio University\'s test-optional pathway. You may send ACT or SAT scores if you want them considered, and you say which on the application. Test-optional applicants are considered for direct admission to programs, merit scholarships and honors programs.',
        source: 'testOptional',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'deadlines',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and fees', amounts: [15018, 26792] },
            { item: 'Housing', amounts: [9064, 9064] },
            { item: 'Food', amounts: [6256, 6256] },
            { item: 'Books and supplies', amounts: [918, 918] },
            { item: 'Transportation', amounts: [2016, 2016] },
            { item: 'Personal expenses', amounts: [1022, 1022] },
        ],
        totals: [34294, 46068],
        note: 'The Athens campus cost of attendance for the 2026-2027 cohort, the latest Ohio posts. Under the OHIO Guarantee, a cohort\'s tuition, fees, housing and food stay fixed for four years; rates for students entering in fall 2027 are not posted yet. Housing is the average residence hall cost for freshmen and food assumes 21 meals a week.',
        source: 'costs',
        netPrice: {
            average: 20666,
            byIncome: [
                { income: '$0 to $30,000', amount: 15142 },
                { income: '$30,001 to $48,000', amount: 14643 },
                { income: '$48,001 to $75,000', amount: 16141 },
                { income: '$75,001 to $110,000', amount: 22188 },
                { income: 'Over $110,000', amount: 24609 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Ohio University\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
